import React, { useContext, useState, useCallback, useMemo } from 'react';

import cx from 'classnames';
import { Button } from 'components';
import { Icon } from 'design-system-web';
import { useTranslation } from 'react-i18next';

import { ContentRendererContext } from 'lane-shared/contexts';
import { hasPermission } from 'lane-shared/helpers';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import useFlag from 'lane-shared/hooks/useFlag';
import useUserDataContext from 'lane-shared/hooks/useUserDataContext';
import { FeatureFlag } from 'constants-flags';
import { PERMISSION_KEYS } from 'constants-permissions';

import useChannelAdminContext from 'hooks/useChannelAdminContext';
import { convertToUUID } from 'uuid-encoding';

import type { VisitorCustomFieldConfig } from 'graphql-query-contracts';

import { useVisitorCustomFieldConfigs } from 'domains/visitorManagement/hooks/useVisitorCustomFieldConfigs';

import { CustomFieldsModal } from './CustomFieldsModal';
import { CustomFieldRenderer } from './CustomFieldRenderer';

import styles from './CustomFieldsSection.scss';

type CustomFieldsSectionProps = {
  className?: string;
  isPreview?: boolean;
};

export function CustomFieldsSection({
  className,
  isPreview = false,
}: CustomFieldsSectionProps) {
  const { t } = useTranslation();
  const contentRendererContext = useContext(ContentRendererContext);
  const userDataContext = useUserDataContext();
  const channelAdminContext = useChannelAdminContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addedFields, setAddedFields] = useState<VisitorCustomFieldConfig[]>(
    []
  );
  const customFieldsEnabled = useFlag(
    FeatureFlag.VisitorManagementCustomFields,
    false
  );

  const editMode = contentRendererContext?.editMode;
  const user = userDataContext?.user;
  const channel = channelAdminContext?.channel;

  const isAdmin =
    user?.isSuperUser ||
    hasPermission(
      user?.roles || [],
      [PERMISSION_KEYS.PERMISSION_ADMIN],
      channel?._id
    );

  const organizationId = channel?._id ? convertToUUID(channel._id) : '';

  const {
    data: enabledCustomFields,
    loading,
    error,
    called,
    fetch: fetchCustomFields,
  } = useVisitorCustomFieldConfigs();

  const handleOpenModal = useCallback(() => {
    fetchCustomFields(organizationId);
    setIsModalOpen(true);
  }, [fetchCustomFields, organizationId]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleAddFields = useCallback(
    (fields: VisitorCustomFieldConfig[]) => {
      setAddedFields(fields);
      setIsModalOpen(false);
    },
    []
  );

  const alreadyAddedFieldIds = useMemo(
    () => addedFields.map(f => f.id),
    [addedFields]
  );

  if (isPreview || !customFieldsEnabled || !editMode || !isAdmin) {
    return null;
  }

  return (
    <>
      {addedFields.length > 0 && (
        <div
          className={styles.addedFieldsContainer}
          data-test="addedFieldsContainer"
        >
          <h3 className={styles.addedFieldsHeading}>
            {t(
              'web.admin.content.features.visitor.management.general.customFields.addedFieldsTitle'
            )}
          </h3>
          {addedFields.map(field => (
            <CustomFieldRenderer key={field.id} field={field} />
          ))}
        </div>
      )}

      <div
        className={cx(styles.customFieldsSection, className)}
        data-test="customFieldsSection"
      >
        <Button
          variant="outlined"
          className={styles.addCustomFieldsButton}
          onClick={handleOpenModal}
          testId="addCustomFieldsButton"
          startIcon={
            <Icon
              name="plus"
              set={ICON_SET_FONTAWESOME}
              testId="addCustomFieldsIcon"
            />
          }
        >
          {t(
            'web.admin.content.features.visitor.management.general.customFields.addButton'
          )}
        </Button>
      </div>

      {isModalOpen && (
        <CustomFieldsModal
          isOpen={isModalOpen}
          customFields={enabledCustomFields}
          loading={loading}
          error={error}
          onClose={handleCloseModal}
          onSave={handleAddFields}
          alreadyAddedFieldIds={alreadyAddedFieldIds}
          hasFetched={called}
        />
      )}
    </>
  );
}
